/**
 * Post-checkout landing (ADR-0022 §4 / success_url).
 *
 * RC Web Billing redirects here after a successful charge. The server is the gate
 * of record: we poll GET /me/entitlement (the shared ENTITLEMENT_KEY, so every
 * gate in the app sees the new state) until isPremium flips true. The RC webhook
 * invalidates the 60 s cache, so confirmation lands within seconds. Once premium,
 * we route the user to the home surface, now unlocked.
 */
import type { Entitlement } from '@nutians/shared/schemas';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { api } from '../api.js';
import { ENTITLEMENT_KEY } from '../hooks/useEntitlement.js';

export default function BillingSuccessPage() {
  const navigate = useNavigate();

  const { data: entitlement } = useQuery({
    queryKey: ENTITLEMENT_KEY,
    queryFn: async (): Promise<Entitlement> => {
      const { data, error } = await api.GET('/me/entitlement');
      if (error || !data) throw new Error('GET /me/entitlement failed');
      return data;
    },
    // Poll every 2 s until premium is confirmed, then stop.
    refetchInterval: (query) => (query.state.data?.isPremium ? false : 2000),
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  const isPremium = entitlement?.isPremium ?? false;

  useEffect(() => {
    if (!isPremium) return;
    const t = setTimeout(() => navigate('/', { replace: true }), 1500);
    return () => clearTimeout(t);
  }, [isPremium, navigate]);

  return (
    <div className="mx-auto flex max-w-md flex-col items-center gap-4 py-16 text-center">
      {isPremium ? (
        <>
          <h2 className="text-xl font-medium tracking-tight text-ink dark:text-ink">
            You&apos;re all set
          </h2>
          <p className="text-sm text-ink-soft dark:text-ink-soft">
            Premium is active. Taking you back to your dashboard…
          </p>
        </>
      ) : (
        <>
          <h2 className="text-xl font-medium tracking-tight text-ink dark:text-ink">
            Confirming your subscription…
          </h2>
          <p className="text-sm text-ink-soft dark:text-ink-soft" aria-live="polite">
            This usually takes a few seconds. You can keep this page open.
          </p>
        </>
      )}
    </div>
  );
}
